import type { PostureAnalysis } from '@/lib/types';
import { compressImageWithMeta } from '../lib/image-compression';
import { scaleCapturedSpatialData } from '../lib/scale-captured-spatial';

/**
 * Removes base64 image blobs from captured views before prospect opt-in.
 * Keeps landmarks, metrics, and classification — enough for admin review and report UI.
 */
export function stripImageDataFromCaptured(
  captured: PostureAnalysis['captured'],
): PostureAnalysis['captured'] {
  if (!captured) return captured;
  const out: PostureAnalysis['captured'] = {};
  for (const [view, entry] of Object.entries(captured)) {
    if (!entry) continue;
    const { imageData: _imageData, ...rest } = entry;
    out[view as keyof PostureAnalysis['captured']] = rest as PostureAnalysis['captured'][typeof view];
  }
  return out;
}

export async function compressCapturedImagesForProspect(
  captured: PostureAnalysis['captured'],
): Promise<PostureAnalysis['captured']> {
  if (!captured) return captured;
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return captured;
  }
  const out: PostureAnalysis['captured'] = {};
  for (const [view, entry] of Object.entries(captured)) {
    if (!entry) continue;
    if (entry.imageData && typeof entry.imageData === 'string') {
      try {
        const meta = await Promise.race([
          compressImageWithMeta(entry.imageData, 800, 0.6),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Compression timeout')), 500),
          ),
        ]);
        // Keep landmarks/custom pins in the same pixel space as the compressed image.
        const scaled = scaleCapturedSpatialData(entry, meta.scale);
        out[view as keyof PostureAnalysis['captured']] = {
          ...scaled,
          imageData: meta.dataUrl,
        } as PostureAnalysis['captured'][typeof view];
      } catch {
        out[view as keyof PostureAnalysis['captured']] = entry;
      }
    } else {
      out[view as keyof PostureAnalysis['captured']] = entry;
    }
  }
  return out;
}

import { getEvaluationRules } from '@/lib/posture-analysis/evaluation-rules-load';

export function isSelfAssessmentPictureSavingEnabled(): boolean {
  if (typeof process !== 'undefined' && process.env) {
    if (process.env.SAVE_SELF_ASSESSMENT_PICTURES === 'false' || process.env.SAVE_SELF_ASSESSMENT_PICTURES === '0') {
      return false;
    }
  }
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const raw = window.localStorage.getItem('app_settings');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.saveSelfAssessmentPictures === false) {
          return false;
        }
      }
    } catch {
      // Ignore storage read errors
    }
  }
  try {
    const rules = getEvaluationRules();
    if (rules.selfAssessment?.savePictures === false) {
      return false;
    }
  } catch {
    // If evaluation-rules.yaml is unavailable in a minimalist mock context, default to true
  }
  return true;
}

export async function buildProspectPosturePayload(
  analysis: PostureAnalysis,
  gdprConsent: boolean = true,
  savePictures: boolean = true,
) {
  const isAllowedByConfig = isSelfAssessmentPictureSavingEnabled();
  const shouldSavePictures = gdprConsent && savePictures && isAllowedByConfig;

  const captured = shouldSavePictures
    ? await compressCapturedImagesForProspect(analysis.captured)
    : stripImageDataFromCaptured(analysis.captured);

  return {
    clientHeight: analysis.clientHeight,
    unit: analysis.unit,
    captured,
    analysis: analysis.analysis,
    ...(analysis.classification ? { classification: analysis.classification } : {}),
  };
}

